import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { furnitureData } from '../data/dummyData';

const AIAnalysisPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const link = location.state?.link || '';
  const serviceType = location.state?.serviceType || 'verify_delivery';

  const [step, setStep] = useState(0); // 0: 링크입력, 1: 분석중, 2: 결과, 3: 가구직접선택
  const [inputLink, setInputLink] = useState(link);
  const [selectedSite, setSelectedSite] = useState('당근마켓');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedFurniture, setSelectedFurniture] = useState('sofa');
  const [customWeight, setCustomWeight] = useState('');
  const [floor, setFloor] = useState('1');
  const [hasElevator, setHasElevator] = useState(true);
  const [sellerFloor, setSellerFloor] = useState('2');
  const [sellerElevator, setSellerElevator] = useState(false);

  // AI 분석 시뮬레이션
  const startAnalysis = () => {
    if (!inputLink.trim()) return;
    setAnalyzing(true);
    setStep(1);
    setTimeout(() => {
      const furniture = furnitureData[selectedFurniture] || furnitureData.sofa;
      setAnalysisResult({
        ...furniture,
        confidence: 92,
        marketPrice: '80,000~150,000원',
        condition: '상태 양호',
        conditionScore: 4.2,
        images: ['📸 상품 이미지 1', '📸 상품 이미지 2', '📸 상품 이미지 3'],
        detectedIssues: furniture.difficulty === '상' ? ['무거운 물품 - 2인 필요', '엘리베이터 확인 필요'] : ['기본 포장 필요'],
        sourceLink: inputLink,
        platform: selectedSite,
      });
      setAnalyzing(false);
      setStep(2);
    }, 2800);
  };

  const goToPricing = () => {
    navigate('/pricing', {
      state: {
        furniture: analysisResult || furnitureData[selectedFurniture],
        serviceType,
        hasElevator,
        floor: parseInt(floor),
        sellerFloor: parseInt(sellerFloor),
        sellerElevator,
      }
    });
  };

  const renderAnalyzeProgress = () => {
    const steps = [
      { label: '링크 분석 중...', icon: '🔗' },
      { label: '상품 이미지 인식 중...', icon: '🖼️' },
      { label: '가구 무게·크기 추정 중...', icon: '📐' },
      { label: '요금 계산 중...', icon: '💰' },
    ];
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>🤖</div>
        <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 8, color: '#1F2937' }}>AI 분석 중</div>
        <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 32 }}>잠시만 기다려주세요</div>

        {steps.map((s, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
            padding: '12px 16px', borderRadius: 12, background: '#F9FAFB',
            opacity: 1, animation: `fadeIn 0.5s ${i * 0.6}s both`
          }}>
            <span style={{ fontSize: 20 }}>{s.icon}</span>
            <span style={{ fontSize: 14, color: '#374151' }}>{s.label}</span>
            <div style={{ marginLeft: 'auto' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', border: '3px solid #E5E7EB', borderTopColor: '#FF6B35', animation: 'spin 0.8s linear infinite' }}/>
            </div>
          </div>
        ))}
        <style>{`
          @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  };

  return (
    <div className="page-white">
      {/* 헤더 */}
      <div style={{ background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)', padding: '20px 20px 24px' }}>
        <button
          onClick={() => step > 0 ? setStep(step - 1) : navigate(-1)}
          style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, padding: '8px 12px', color: 'white', cursor: 'pointer', marginBottom: 12 }}
        >
          ← 뒤로
        </button>
        <div style={{ color: 'white', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
          🤖 AI 가구 분석
        </div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
          링크 하나로 무게·크기·요금 자동 계산
        </div>
      </div>

      {/* Step 0: 링크 입력 */}
      {step === 0 && (
        <div style={{ padding: '24px 20px' }}>
          {/* 직거래 사이트 선택 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 10 }}>
              📱 어느 사이트의 상품인가요?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['당근마켓', '중고나라', '번개장터', '직접 입력'].map(site => (
                <button
                  key={site}
                  onClick={() => setSelectedSite(site)}
                  style={{
                    flex: 1, padding: '10px 4px', border: `2px solid ${selectedSite === site ? '#FF6B35' : '#E5E7EB'}`,
                    borderRadius: 10, background: selectedSite === site ? '#FFF0EB' : 'white',
                    color: selectedSite === site ? '#FF6B35' : '#6B7280',
                    fontWeight: 600, fontSize: 11, cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {site}
                </button>
              ))}
            </div>
          </div>

          {/* 링크 입력 */}
          <div className="input-group">
            <label className="input-label">🔗 상품 링크 붙여넣기</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="input-field"
                value={inputLink}
                onChange={e => setInputLink(e.target.value)}
                placeholder="https://www.daangn.com/articles/..."
                style={{ flex: 1 }}
              />
              <button
                onClick={startAnalysis}
                disabled={!inputLink.trim()}
                style={{
                  background: inputLink.trim() ? '#FF6B35' : '#E5E7EB',
                  color: inputLink.trim() ? 'white' : '#9CA3AF',
                  border: 'none', borderRadius: 10, padding: '0 16px',
                  fontWeight: 700, cursor: inputLink.trim() ? 'pointer' : 'not-allowed', fontSize: 14
                }}
              >
                분석
              </button>
            </div>
          </div>

          {/* 가구 직접 선택 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 4 }}>
              🛋️ 가구 종류 선택
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>
              링크 없이도 선택하면 AI가 요금을 계산해 드려요
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {Object.entries(furnitureData).map(([key, data]) => (
                <button
                  key={key}
                  onClick={() => setSelectedFurniture(key)}
                  style={{
                    padding: '12px 8px', border: `2px solid ${selectedFurniture === key ? '#FF6B35' : '#E5E7EB'}`,
                    borderRadius: 12, background: selectedFurniture === key ? '#FFF0EB' : 'white',
                    color: selectedFurniture === key ? '#FF6B35' : '#4B5563',
                    fontWeight: selectedFurniture === key ? 700 : 500,
                    fontSize: 12, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center'
                  }}
                >
                  {key === 'sofa' ? '🛋️' : key === 'wardrobe' ? '🗄️' : key === 'bed' ? '🛏️' :
                   key === 'desk' ? '🖥️' : key === 'refrigerator' ? '🧊' :
                   key === 'dining' ? '🍽️' : '📚'}<br/>
                  <span style={{ fontSize: 11 }}>{data.name.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 위치 정보 */}
          <div style={{ background: '#F9FAFB', borderRadius: 16, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937', marginBottom: 12 }}>
              🏢 층수 및 엘리베이터 정보
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 6 }}>판매자 층수</div>
                <select
                  value={sellerFloor}
                  onChange={e => setSellerFloor(e.target.value)}
                  className="input-field"
                  style={{ padding: '10px 12px', fontSize: 13 }}
                >
                  {[...Array(30)].map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1}층</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 6 }}>나의 층수</div>
                <select
                  value={floor}
                  onChange={e => setFloor(e.target.value)}
                  className="input-field"
                  style={{ padding: '10px 12px', fontSize: 13 }}
                >
                  {[...Array(30)].map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1}층</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 6 }}>판매자 엘리베이터</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['있음', '없음'].map(v => (
                    <button
                      key={v}
                      onClick={() => setSellerElevator(v === '있음')}
                      style={{
                        flex: 1, padding: '9px 0', border: `2px solid ${sellerElevator === (v === '있음') ? '#27AE60' : '#E5E7EB'}`,
                        borderRadius: 8, background: sellerElevator === (v === '있음') ? '#e8f8ef' : 'white',
                        color: sellerElevator === (v === '있음') ? '#27AE60' : '#6B7280',
                        fontWeight: 600, fontSize: 12, cursor: 'pointer'
                      }}
                    >{v}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 6 }}>내 집 엘리베이터</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['있음', '없음'].map(v => (
                    <button
                      key={v}
                      onClick={() => setHasElevator(v === '있음')}
                      style={{
                        flex: 1, padding: '9px 0', border: `2px solid ${hasElevator === (v === '있음') ? '#27AE60' : '#E5E7EB'}`,
                        borderRadius: 8, background: hasElevator === (v === '있음') ? '#e8f8ef' : 'white',
                        color: hasElevator === (v === '있음') ? '#27AE60' : '#6B7280',
                        fontWeight: 600, fontSize: 12, cursor: 'pointer'
                      }}
                    >{v}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              if (inputLink.trim()) {
                startAnalysis();
              } else {
                const furniture = furnitureData[selectedFurniture];
                setAnalysisResult({ ...furniture });
                setStep(2);
              }
            }}
            style={{
              width: '100%', padding: '16px', borderRadius: 16,
              background: '#FF6B35', color: 'white', border: 'none',
              fontWeight: 800, fontSize: 16, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(255,107,53,0.4)'
            }}
          >
            {inputLink.trim() ? '🤖 AI 분석 시작' : '🛋️ 가구 선택으로 계속'}
          </button>
        </div>
      )}

      {/* Step 1: 분석 중 */}
      {step === 1 && renderAnalyzeProgress()}

      {/* Step 2: 분석 결과 */}
      {step === 2 && analysisResult && (
        <div style={{ padding: '24px 20px 100px' }}>
          {/* AI 분석 결과 헤더 */}
          <div style={{
            background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
            borderRadius: 16, padding: '20px', marginBottom: 16, color: 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 28 }}>🤖</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18 }}>AI 분석 완료!</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>신뢰도 {analysisResult.confidence || 92}%</div>
              </div>
              <div style={{
                marginLeft: 'auto', background: '#27AE60', borderRadius: 20,
                padding: '4px 12px', fontSize: 12, fontWeight: 700
              }}>
                ✓ 완료
              </div>
            </div>

            {/* 신뢰도 바 */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, opacity: 0.7 }}>분석 신뢰도</span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{analysisResult.confidence || 92}%</span>
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 3 }}>
                <div style={{ width: `${analysisResult.confidence || 92}%`, height: '100%', background: '#27AE60', borderRadius: 3 }}/>
              </div>
            </div>
          </div>

          {/* 가구 정보 */}
          <div style={{ background: 'white', borderRadius: 16, padding: 20, marginBottom: 16, border: '1px solid #F3F4F6', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1F2937', marginBottom: 14 }}>
              🛋️ 상품 분석 결과
            </div>
            {[
              { label: '상품명', val: analysisResult.name },
              { label: '예상 무게', val: analysisResult.weight },
              { label: '크기 (가로×깊이×높이)', val: analysisResult.size },
              { label: '운반 난이도', val: analysisResult.difficulty, badge: true },
              { label: '2인 작업 필요', val: analysisResult.twoPersonRequired ? '✅ 필요' : '❌ 불필요' },
              { label: '권장 차량', val: analysisResult.recommendedVehicle },
              { label: '예상 작업 시간', val: `${analysisResult.estimatedTime}분` },
            ].map((item, i) => (
              <div key={i} className="info-row">
                <span className="info-key">{item.label}</span>
                {item.badge ? (
                  <span style={{
                    padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                    background: item.val === '상' ? '#fde8e8' : item.val === '중' ? '#fef9e7' : '#e8f8ef',
                    color: item.val === '상' ? '#E74C3C' : item.val === '중' ? '#F39C12' : '#27AE60'
                  }}>{item.val}</span>
                ) : (
                  <span className="info-val">{item.val}</span>
                )}
              </div>
            ))}
          </div>

          {/* 주의 사항 */}
          {analysisResult.detectedIssues?.length > 0 && (
            <div style={{ background: '#FFF9E6', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid #FFE0A0' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#B7791F', marginBottom: 8 }}>
                ⚠️ 확인 사항
              </div>
              {analysisResult.detectedIssues.map((issue, i) => (
                <div key={i} style={{ fontSize: 13, color: '#92400E', marginBottom: 4, display: 'flex', gap: 6 }}>
                  <span>•</span><span>{issue}</span>
                </div>
              ))}
            </div>
          )}

          {/* 서비스 타입 안내 */}
          <div style={{ background: '#FFF0EB', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #FFD0B5' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#FF6B35', marginBottom: 8 }}>
              🎯 선택하신 서비스
            </div>
            <div style={{ fontSize: 13, color: '#92400E' }}>
              {serviceType === 'verify' && '✅ 현장 검증 서비스 - 하자 확인 후 구매 결정'}
              {serviceType === 'delivery' && '🚚 용달 배송 서비스 - 검증 완료 상품 배송'}
              {serviceType === 'verify_delivery' && '🎯 검증 + 배송 패키지 - 한번에 해결!'}
            </div>
          </div>

          <button
            onClick={goToPricing}
            style={{
              width: '100%', padding: '16px', borderRadius: 16,
              background: '#FF6B35', color: 'white', border: 'none',
              fontWeight: 800, fontSize: 16, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(255,107,53,0.4)'
            }}
          >
            💰 요금 확인하기 →
          </button>
        </div>
      )}
    </div>
  );
};

export default AIAnalysisPage;
